import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, searchMatch, getResponsiveThumbnailUrl } from '@/lib/utils';
import { Search, Package, MapPin, User, Gavel, TrendingUp, Clock, Loader2 } from 'lucide-react';
import { listingsAPI, settingsAPI } from '@/lib/api';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import ListingCard from '@/components/listings/ListingCard';

const STORAGE_KEY = 'alllistings_state';
const SCROLL_KEY = 'alllistings_scroll';
const LOCKED_SCROLL_KEY = 'alllistings_locked_scroll'; // Locked at navigation time

const AllListingsPage = () => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Track if we're coming from a different page
    const prevPathRef = useRef(location.pathname);
    const isNewVisit = useRef(true);
    
    // Clear saved state when navigating TO this page (not within it)
    useEffect(() => {
        // Check if this is a fresh navigation to this page
        const navFlag = sessionStorage.getItem('nav_to_all_listings');
        if (navFlag) {
            sessionStorage.removeItem('nav_to_all_listings');
            sessionStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(SCROLL_KEY);
            sessionStorage.removeItem(LOCKED_SCROLL_KEY);
            isNewVisit.current = true;
        }
    }, []);
    
    // Try to restore state from sessionStorage
    const getSavedState = () => {
        if (isNewVisit.current) {
            isNewVisit.current = false;
            return null;
        }
        try {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            if (saved) {
                const state = JSON.parse(saved);
                // Check if state is still valid (max 10 minutes old)
                if (state && state.listings?.length > 0 && Date.now() - state.timestamp < 10 * 60 * 1000) {
                    return state;
                }
            }
        } catch (e) {
            // Ignore
        }
        return null;
    };
    
    const savedState = useRef(getSavedState());
    const [listings, setListings] = useState(savedState.current?.listings || []);
    const [loading, setLoading] = useState(!savedState.current);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(savedState.current?.currentPage || 1);
    const [itemsPerPage, setItemsPerPage] = useState(48);
    const [totalItems, setTotalItems] = useState(savedState.current?.totalItems || 0);
    const [hasMore, setHasMore] = useState(savedState.current?.hasMore ?? true);
    // Restore grid settings from saved state to prevent layout flash
    const [gridSettings, setGridSettings] = useState(savedState.current?.gridSettings || {
        grid_columns_desktop: 4,
        grid_columns_tablet: 3,
        grid_columns_mobile: 2,
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    const loadMoreRef = useRef(null);
    const shouldFetchRef = useRef(!savedState.current); // Only fetch if no saved state
    const scrollRestoredRef = useRef(false);

    // Detect mobile
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Check if single column mode
    const singleColumn = isMobile && gridSettings.grid_columns_mobile === 1;

    // Disable browser's automatic scroll restoration - we handle it manually
    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        return () => {
            if ('scrollRestoration' in window.history) {
                window.history.scrollRestoration = 'auto';
            }
        };
    }, []);

    // Restore scroll position AFTER listings are rendered
    useLayoutEffect(() => {
        if (scrollRestoredRef.current || listings.length === 0) return;
        
        // Use locked scroll if available (more reliable), otherwise use regular scroll key
        let savedScroll = sessionStorage.getItem(LOCKED_SCROLL_KEY);
        if (savedScroll) {
            sessionStorage.removeItem(LOCKED_SCROLL_KEY); // Clear after reading
        } else {
            savedScroll = sessionStorage.getItem(SCROLL_KEY);
        }
        
        if (savedScroll) {
            const scrollY = parseInt(savedScroll, 10);
            if (scrollY > 0) {
                // Immediate scroll attempt for faster restoration
                window.scrollTo(0, scrollY);
                
                // Then retry with RAF to handle dynamic content
                const attemptScroll = (attempts = 0) => {
                    if (attempts > 30) {
                        scrollRestoredRef.current = true;
                        return;
                    }
                    
                    const currentScroll = window.scrollY;
                    const targetScroll = scrollY;
                    
                    // If we're close enough, we're done
                    if (Math.abs(currentScroll - targetScroll) < 100) {
                        scrollRestoredRef.current = true;
                        return;
                    }
                    
                    window.scrollTo(0, targetScroll);
                    requestAnimationFrame(() => attemptScroll(attempts + 1));
                };
                
                // Start immediately, no delay
                attemptScroll();
            } else {
                scrollRestoredRef.current = true;
            }
        } else {
            scrollRestoredRef.current = true;
        }
    }, [listings.length]);

    // Save scroll position continuously and lock on navigation
    useEffect(() => {
        let ticking = false;
        let navigationPending = false;
        
        const handleScroll = () => {
            if (navigationPending) return;
            
            if (!ticking) {
                requestAnimationFrame(() => {
                    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
                    ticking = false;
                });
                ticking = true;
            }
        };
        
        // Lock scroll position on mousedown before navigation
        const handleMouseDown = (e) => {
            const link = e.target.closest('a[href^="/listing/"], a[href^="/category/"], a[href^="/user/"]');
            if (link) {
                navigationPending = true;
                const currentScrollY = window.scrollY;
                sessionStorage.setItem(LOCKED_SCROLL_KEY, String(currentScrollY));
                sessionStorage.setItem(SCROLL_KEY, String(currentScrollY));
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('mousedown', handleMouseDown, { capture: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleMouseDown, { capture: true });
        };
    }, []);

    // Save listings state when it changes (including grid settings)
    useEffect(() => {
        if (listings.length === 0) return;
        
        const state = {
            listings,
            currentPage,
            hasMore,
            totalItems,
            gridSettings,
            timestamp: Date.now()
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }, [listings, currentPage, hasMore, totalItems, gridSettings]);

    // Load settings (skip if we have saved gridSettings)
    useEffect(() => {
        // Skip loading if we restored gridSettings from sessionStorage
        if (savedState.current?.gridSettings) return;
        
        const loadSettings = async () => {
            try {
                const res = await settingsAPI.get();
                setItemsPerPage(res.data.listings_per_page || 48);
                setGridSettings({
                    grid_columns_desktop: res.data.grid_columns_desktop || 4,
                    grid_columns_tablet: res.data.grid_columns_tablet || 3,
                    grid_columns_mobile: res.data.grid_columns_mobile || 2,
                });
            } catch (error) {
                console.error('Error loading settings:', error);
            }
        };
        loadSettings();
    }, []);

    // Fetch listings - infinite scroll for both mobile and desktop
    useEffect(() => {
        // Skip fetch if we restored from saved state and it's the first page
        if (!shouldFetchRef.current && currentPage === 1) {
            return;
        }
        
        const fetchListings = async () => {
            if (currentPage > 1) {
                // Append mode for subsequent pages
                setLoadingMore(true);
            } else {
                // Initial load
                setLoading(true);
            }
            
            try {
                const skip = (currentPage - 1) * itemsPerPage;
                const res = await listingsAPI.getAll({ 
                    status: 'active',
                    skip,
                    limit: itemsPerPage
                });
                const data = res.data?.listings || res.data || [];
                const total = res.data?.total || data.length;
                
                if (currentPage > 1) {
                    // Append to existing listings
                    setListings(prev => [...prev, ...data]);
                } else {
                    // Initial: replace listings
                    setListings(data);
                }
                
                setTotalItems(total);
                setHasMore(skip + data.length < total);
                
                // Mark that we should fetch on subsequent page changes
                shouldFetchRef.current = true;
            } catch (error) {
                console.error('Error fetching listings:', error);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        };
        fetchListings();
    }, [currentPage, itemsPerPage]);

    // Infinite scroll observer for both mobile and desktop
    useEffect(() => {
        if (!hasMore || loading || loadingMore) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    setCurrentPage(prev => prev + 1);
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );
        
        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }
        
        return () => observer.disconnect();
    }, [hasMore, loading, loadingMore]);

    // Clean up scrollToPagination param
    useEffect(() => {
        const shouldScroll = searchParams.get('scrollToPagination') === 'true';
        if (shouldScroll && !loading) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('scrollToPagination');
            setSearchParams(newParams, { replace: true });
        }
    }, [loading, searchParams, setSearchParams]);

    const filteredListings = listings.filter(l => 
        !searchQuery || 
        searchMatch(l.title, searchQuery) ||
        searchMatch(l.category_name, searchQuery)
    );

    const getTimeRemaining = (endDate) => {
        if (!endDate) return null;
        const end = new Date(endDate);
        const now = new Date();
        const diff = end - now;
        
        if (diff <= 0) return 'Încheiat';
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) return `${days}z ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    if (loading && listings.length === 0) {
        return (
            <div className="container mx-auto py-8 px-4">
                <Skeleton className="h-12 w-64 mb-6" />
                <Skeleton className="h-10 w-full mb-6" />
                <div 
                    className="grid gap-6 category-grid"
                    style={{
                        '--grid-cols-mobile': gridSettings.grid_columns_mobile,
                        '--grid-cols-tablet': gridSettings.grid_columns_tablet,
                        '--grid-cols-desktop': gridSettings.grid_columns_desktop,
                    }}
                >
                    {[...Array(8)].map((_, i) => (
                        <Skeleton key={i} className="h-[340px] rounded-2xl" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Package className="w-8 h-8 text-primary" />
                        Toate anunțurile
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {totalItems} anunțuri disponibile
                    </p>
                </div>
            </div>

            {filteredListings.length === 0 && !loading ? (
                <div className="text-center py-16 text-muted-foreground">
                    <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Nu există anunțuri disponibile momentan</p>
                </div>
            ) : (
                <>
                    <div 
                        className="grid gap-6 category-grid"
                        style={{
                            '--grid-cols-mobile': gridSettings.grid_columns_mobile,
                            '--grid-cols-tablet': gridSettings.grid_columns_tablet,
                            '--grid-cols-desktop': gridSettings.grid_columns_desktop,
                        }}
                    >
                        {filteredListings.map((listing) => (
                            <ListingCard 
                                key={listing.listing_id} 
                                listing={listing} 
                                singleColumn={singleColumn}
                            />
                        ))}
                    </div>
                    
                    {/* Infinite scroll loader for all devices */}
                    <div 
                        ref={loadMoreRef} 
                        className="flex justify-center py-8"
                        data-testid="infinite-scroll-trigger"
                    >
                        {loadingMore && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Se încarcă...</span>
                            </div>
                        )}
                        {!hasMore && listings.length > 0 && (
                            <p className="text-muted-foreground text-sm">
                                Ai văzut toate cele {totalItems} anunțuri
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AllListingsPage;
